import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { Subject, Observable, of } from 'rxjs';
import { debounceTime, shareReplay, map } from 'rxjs/operators';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { GenericErrorStateMatcher } from 'src/app/shared/attribute-state-generators/generic-error.state-matcher';
import { environment } from 'src/environments/environment';
import { CommonHelpers } from 'src/app/shared/helpers/common.helpers';

@Component({
  selector: 'app-mailing-address-autocomplete',
  templateUrl: './mailing-address-autocomplete.component.html',
  styleUrls: ['./mailing-address-autocomplete.component.scss']
})
export class MailingAddressAutocompleteComponent implements OnInit, OnChanges {
  @Input() isDisabled = false;
  @Input('errorStateMatcher') errorStateMatcher: ErrorStateMatcher;
  @Input('groupName') groupName: FormGroup;
  @Input('controlName') controlName: string;
  @Input('mailingAddressPreviouslyVerified') mailingAddressPreviouslyVerified: boolean;
  @Input('onlyIncludeNyAddresses') onlyIncludeNyAddresses: boolean = false;
  @Input('doNotAllowPOBoxes') doNotAllowPOBoxes: boolean = false;
  @Output() mailingAddressValidated: EventEmitter<{ mailingAddress: MailingAddress, address: any }> =
    new EventEmitter<{ mailingAddress: MailingAddress, address: any }>();
  @Output() mailingAddressFocus: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('mailingAddressInput', null) mailingAddressInput;
  @ViewChild('autocomplete', null) autocomplete: MatAutocomplete;

  mailingAddressSuggestions: Observable<any>;
  mailingAddressSuggestionsSubject = new Subject<string>();
  mailingAddressVerified = false;

  constructor(private http: HttpClient) { }

  ngOnInit() {
    console.log('groupName');
    console.log(this.groupName);
    this.errorStateMatcher = !this.errorStateMatcher || this.errorStateMatcher === undefined ?
      new GenericErrorStateMatcher() : this.errorStateMatcher;

    const subscription = this.mailingAddressSuggestionsSubject.pipe(
      shareReplay(1),
      debounceTime(400)).subscribe((val) => {
        // Get suggestions each time the mailing address changes...
        this.getMailingAddressSuggestions();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['mailingAddressPreviouslyVerified']) {
      // Determine if the mailing address has already been verified...
      this.mailingAddressVerified = this.mailingAddressPreviouslyVerified;
    }
  }

  getMailingAddressSuggestions(secondarySearchText?: any, manuallyDisplaySuggestions: boolean = false) {
    // Check if a mailing address was entered...
    if (this.groupName.get(this.controlName).value) {
      const params: URLSearchParams = new URLSearchParams();

      // set Smarty Streets parameter values. By default, when using freeform address input the
      // maximum number of valid USPS addresses returned is one (1)...
      params.set('key', environment.smartyStreetsKey);
      params.set('search', this.groupName.get(this.controlName).value);

      // Check if only NY addresses should be used...
      if (this.onlyIncludeNyAddresses) {
        params.set('include_only_states', 'NY')
      }

      // Call Smarty Streets api to validate address...
      this.mailingAddressSuggestions = this.http.get('https://us-autocomplete-pro.api.smartystreets.com/lookup?' + params +
        (secondarySearchText || '')).pipe(
          shareReplay(1),
          map((data: [any]) => {
            // Check if any valid address suggestions were found...
            if (data['suggestions']) {
              // Return suggestions...
              return data['suggestions'].map(data => {
                // Construct mailing address object...
                return this.constructMailingAddressSuggestion(data);
              });
            } else {
              // No valid address suggestions found...
              return [];
            }
          }));

      // Listen for data...
      this.mailingAddressSuggestions.subscribe(result => {
        // Check if we should manually display suggestions (opening if user is not directly interacting
        // with the input, eg. 'same as' checkbox or secondary address lookup)...
        if (manuallyDisplaySuggestions) {
          // Force autocomplete options to display... 
          this.mailingAddressInput.nativeElement.blur();
          this.mailingAddressInput.nativeElement.focus();
          this.groupName.get(this.controlName).markAsTouched();
        }
      });

    } else {
      // No mailing address entered...
      this.mailingAddressSuggestions = of([]);
    }
  }

  displayFn(suggestion) {
    return this.groupName.get(this.controlName).value;
  }

  onBlur(event) {
    // Check if blur event was fired by MatAutoComplete...
    if (event.relatedTarget && event.relatedTarget.id.indexOf('mat-option') > -1) {
      // Event was fired by the MatAutoComplete selection. Ignore since that event will fire after this...
    } else {
      // Event was not fired by MatAutoComplete, verify the address...
      this.verifyMailingAddress(this.groupName.get(this.controlName).value);
    }

  }

  onMailingAddressSelected(mailingAddressSuggestion) {
    // Check if multiple secondary address entries were found...
    if (mailingAddressSuggestion.option.value.entries > 1) {
      // Set full address using suggested address (excluding # of entries and subsequent data)....
      this.groupName.get(this.controlName).setValue(mailingAddressSuggestion.option.value.street_line + ' ' +
        mailingAddressSuggestion.option.value.secondary);

      // Construct secondary address search string for SmartyStreets...
      const secondarySearchText = '&selected=' +
        mailingAddressSuggestion.option.value.street_line + '+' +
        mailingAddressSuggestion.option.value.secondary + '+(' +
        mailingAddressSuggestion.option.value.entries + ')+' +
        mailingAddressSuggestion.option.value.city + '+' +
        mailingAddressSuggestion.option.value.state + '+' +
        mailingAddressSuggestion.option.value.zip;

      // Run additional address lookup for secondary addresses...
      this.getMailingAddressSuggestions(secondarySearchText.replace(/\s/g, '+'), true);

    } else {
      // Place suggested address inside input...
      this.groupName.get(this.controlName).setValue(mailingAddressSuggestion.option.value.full_address);

      // Clear mailing address suggestions...
      this.mailingAddressSuggestions = of([]);

      // Validate address...
      this.verifyMailingAddress(this.groupName.get(this.controlName).value, false, true);
    }
  }

  constructMailingAddress(address, isValid: boolean = true) {
    // Construct mailing address object...
    return {
      latitude: address.metadata.latitude,
      line1: address.delivery_line_1,
      line2: address.delivery_line_2,
      longitude: address.metadata.longitude,
      city: address.components.city_name,
      state: address.components.state_abbreviation,
      zipcode: address.components.zipcode,
      zipCodeExtension: address.components.plus4_code,
      fullAddress: (address.delivery_line_1 ? address.delivery_line_1 + ' ' : '') +
        (address.delivery_line_2 ? address.delivery_line_2 + ' ' : '') +
        (address.last_line ? address.last_line : ''),
      isInactive: address.analysis.active === 'N',
      isPOBox: address.metadata.record_type === 'P',
      isSecondaryInfoMissing: address.analysis.dpv_match_code === 'D',
      isValid: isValid
    } as MailingAddress;
  }

  constructMailingAddressSuggestion(suggestion) {
    let whiteSpace = '';
    let secondaryDesription = '';

    if (suggestion['secondary']) {
      // Check if multiple entries were found for secondary address...
      if (suggestion['entries'] > 1) {
        // Append number of entries to secondary address...
        secondaryDesription = suggestion['secondary'] + ' (' + suggestion['entries'] + ' entries)';
      } else {
        secondaryDesription = suggestion['secondary']
      }
      whiteSpace = ' ';
    }

    return {
      city: suggestion['city'],
      entries: suggestion['entries'],
      full_address: suggestion['street_line'] + whiteSpace + secondaryDesription + ' ' +
        suggestion['city'] + ', ' + suggestion['state'] + ' ' + suggestion['zipcode'],
      secondary: suggestion['secondary'],
      secondaryDesc: secondaryDesription,
      state: suggestion['state'],
      street_line: suggestion['street_line'],
      zip: suggestion['zipcode']
    } as MailingAddressSuggestion;
  }

  verifyMailingAddress(inputAddress: string, runSuggestionsOnInvalidAddress: boolean = false, forceValidation: boolean = false) {
    if (!CommonHelpers.getBooleanValue(this.mailingAddressVerified) || forceValidation) {
      const params: URLSearchParams = new URLSearchParams();

      // Mailing address is being verified, mark it as unverified...
      this.mailingAddressVerified = false;

      // Check if a mailing address was input/selected...
      if (inputAddress != null && inputAddress.length > 0) {
        // set Smarty Streets parameter values. By default, when using freeform address input the
        // maximum number of valid USPS addresses returned is one (1)...
        params.set('key', environment.smartyStreetsKey);
        params.set('street', inputAddress);

        // Call Smarty Streets api to validate address...
        this.http.get('https://us-street.api.smartystreets.com/street-address?' + params).subscribe
          ((addressMatches: any) => {
            // Make sure only a single address match was found...
            if (addressMatches.length === 1) {
              // Loop through each address match returned...
              addressMatches.forEach(address => {
                // Check if this is a valid active USPS address (USPS delivers mail to this address).
                // Y = The entire address entered was an exact match.
                // S = The address was confirmed without using the secondary info provided (apt#, etc.). The secondary
                //     info is not part of the official address but is left intact in the delivery line as useful info
                //     for the mail carrier.
                if (
                  (address.analysis.dpv_match_code === 'Y' ||
                    address.analysis.dpv_match_code === 'S') &&
                  (!this.onlyIncludeNyAddresses || address.components.state_abbreviation === 'NY') &&
                  (!this.doNotAllowPOBoxes || address.metadata.record_type !== 'P')) {

                  // An address match was found...
                  this.mailingAddressVerified = true;
                  // this.mailingAddressSelected.emit(address);
                  const mailingAddress = this.constructMailingAddress(address);
                  this.mailingAddressValidated.emit({ mailingAddress, address });

                } else {
                  // Emit event to show address was run through validation but failed...
                  const mailingAddress = this.constructMailingAddress(address, false);
                  this.mailingAddressValidated.emit({ mailingAddress, address });
                }
              });
            } else {
              // Emit event to show address was run through
              //  validation but failed (no address returned in event)...
              this.mailingAddressValidated.emit(null);
            }

            // Check if suggestions should be displayed for invalid addresses...
            if (!this.mailingAddressVerified && runSuggestionsOnInvalidAddress) {
              this.getMailingAddressSuggestions(null, true);
            }
          });
      }
    }
  }

  onMailingAddressChanged() {
    // Mailing address has been changed, mark it as unverified...
    this.mailingAddressVerified = false;
  }
}

export interface MailingAddress {
  city: string;
  fullAddress: string;
  isInactive: boolean;
  isPOBox: boolean;
  isSecondaryInfoMissing: boolean;
  isValid: boolean;
  latitude: string;
  line1: string;
  line2: string;
  longitude: string;
  state: string;
  zipcode: string;
  zipCodeExtension: string;
}

export interface MailingAddressSuggestion {
  city: string;
  entries: number;
  full_address: string;
  secondary: string;
  secondaryDesc: string;
  state: string;
  street_line: string;
  zip: string;
}

